/**
 * @fileoverview ReservaInventarioRepository - Repository for inventory reservations management
 */

import { DataRepository } from './base/DataRepository';
import type {
  ReservaInventario,
  ReservaInventarioExpanded,
  CreateReservaInventarioData,
  UpdateReservaInventarioData,
} from '../types/collections/ReservaInventario';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from './base/DataRepository';
import { createModuleLogger } from '../utils/logger';

/**
 * Repository for managing inventory reservations (reserva_inventario collection)
 * Extends DataRepository with domain-specific methods
 */
export class ReservaInventarioRepository extends DataRepository<ReservaInventario> {
  private logger = createModuleLogger('ReservaInventarioRepository');

  constructor() {
    super('reserva_inventario');
  }

  /**
   * Get active reservations (not completed and active)
   */
  async getActive(
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<ReservaInventarioExpanded>> {
    try {
      const result = await this.getList(page, pageSize, {
        filter: 'completado=false && activo=true',
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta',
        sort: '-created',
      });

      this.logger.debug('Fetched active reservas', { count: result.items.length });
      return result as ListResult<ReservaInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener reservas activas: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ACTIVE_ERROR'
      );
    }
  }

  /**
   * Get reservations for a specific order
   */
  async getByOrden(
    ordenId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<ReservaInventarioExpanded>> {
    try {
      const result = await this.getList(page, pageSize, {
        filter: `orden_produccion="${ordenId}" && activo=true`,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta,usuario',
        sort: '-created',
      });

      this.logger.debug('Fetched reservas for order', { ordenId, count: result.items.length });
      return result as ListResult<ReservaInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener reservas de la orden ${ordenId}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_ORDEN_ERROR'
      );
    }
  }

  /**
   * Get reservations for multiple orders in a single call
   */
  async getByOrdenes(ordenIds: string[]): Promise<ListResult<ReservaInventarioExpanded>> {
    if (!ordenIds || ordenIds.length === 0) {
      return { page: 1, perPage: 0, totalItems: 0, totalPages: 0, items: [] };
    }

    const filterOr = ordenIds.map(id => `orden_produccion="${id}"`).join(' || ');
    try {
      const result = await this.getAll({
        filter: `(${filterOr}) && activo=true`,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta,usuario',
        sort: '-created'
      });

      return {
        page: 1,
        perPage: result.length,
        totalItems: result.length,
        totalPages: 1,
        items: result as unknown as ReservaInventarioExpanded[]
      };
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener reservas para múltiples órdenes: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_ORDENES_ERROR'
      );
    }
  }

  /**
   * Get reservation for a specific order and producto
   */
  async getByOrdenAndProducto(
    ordenId: string,
    productoId: string
  ): Promise<ReservaInventarioExpanded | null> {
    try {
      const result = await this.findFirst(
        `orden_produccion="${ordenId}" && producto_inventario="${productoId}" && activo=true`,
        {
          expand: 'orden_produccion,producto_inventario,producto_inventario.tinta',
        }
      );

      if (result) {
        this.logger.debug('Found reserva', { ordenId, productoId });
        return result as ReservaInventarioExpanded;
      }

      return null;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener reserva para orden ${ordenId} y producto ${productoId}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_ORDEN_PRODUCTO_ERROR'
      );
    }
  }

  /**
   * Get reservations by producto_inventario
   */
  async getByProductoInventario(
    productoId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<ReservaInventarioExpanded>> {
    try {
      const result = await this.getList(page, pageSize, {
        filter: `producto_inventario="${productoId}" && activo=true`,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta',
        sort: '-created',
      });

      this.logger.debug('Fetched reservas for producto', {
        productoId,
        count: result.items.length,
      });
      return result as ListResult<ReservaInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener reservas del producto ${productoId}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_PRODUCTO_ERROR'
      );
    }
  }

  /**
   * Create a new reserva with expanded relations
   */
  async createWithExpand(
    data: CreateReservaInventarioData
  ): Promise<ReservaInventarioExpanded> {
    try {
      const created = await this.create(data);
      return this.getById(created.id, {
        expand: 'orden_produccion,producto_inventario',
      }) as Promise<ReservaInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al crear reserva: ${error instanceof Error ? error.message : String(error)}`,
        'CREATE_ERROR'
      );
    }
  }

  /**
   * Mark reserva as completado
   */
  async markAsCompleted(id: string): Promise<ReservaInventarioExpanded> {
    try {
      const updated = await this.update(id, { completado: true });
      return this.getById(updated.id, {
        expand: 'orden_produccion,producto_inventario',
      }) as Promise<ReservaInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al marcar reserva como completada: ${error instanceof Error ? error.message : String(error)}`,
        'MARK_COMPLETED_ERROR'
      );
    }
  }

  /**
   * Soft delete by marking as inactive
   */
  async softDelete(id: string): Promise<boolean> {
    try {
      await this.update(id, { activo: false });
      this.logger.debug('Soft deleted reserva', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al eliminar reserva: ${error instanceof Error ? error.message : String(error)}`,
        'SOFT_DELETE_ERROR'
      );
    }
  }

  /**
   * Get reservations for report with filters
   * @param filters - Filter object with date range, usuario, orden, producto, tipo
   */
  async getReservasParaReporte(filters: {
    fechaInicio?: { inicio: string | null; fin: string | null };
    totalMin?: number | null;
    totalMax?: number | null;
    usuario?: string | null;
    orden?: string | null;
    producto?: string | null;
    tipo?: 'devolucion' | 'solicitud' | 'ambas' | null;
  }): Promise<ReservaInventarioExpanded[]> {
    try {
      const filterParts: string[] = [];

      // Filter by date range (created field)
      if (filters.fechaInicio?.inicio && filters.fechaInicio?.fin) {
        const inicio = new Date(filters.fechaInicio.inicio).toISOString();
        const fin = new Date(filters.fechaInicio.fin + 'T23:59:59').toISOString();
        filterParts.push(`created >= "${inicio}" && created <= "${fin}"`);
      } else if (filters.fechaInicio?.inicio) {
        const inicio = new Date(filters.fechaInicio.inicio).toISOString();
        filterParts.push(`created >= "${inicio}"`);
      } else if (filters.fechaInicio?.fin) {
        const fin = new Date(filters.fechaInicio.fin + 'T23:59:59').toISOString();
        filterParts.push(`created <= "${fin}"`);
      }

      // Filter by total range
      if (filters.totalMin !== null && filters.totalMin !== undefined) {
        filterParts.push(`total >= ${filters.totalMin}`);
      }
      if (filters.totalMax !== null && filters.totalMax !== undefined) {
        filterParts.push(`total <= ${filters.totalMax}`);
      }

      // Filter by usuario
      if (filters.usuario) {
        filterParts.push(`usuario = "${filters.usuario}"`);
      }

      // Filter by orden
      if (filters.orden) {
        filterParts.push(`orden_produccion = "${filters.orden}"`);
      }

      // Filter by producto
      if (filters.producto) {
        filterParts.push(`producto_inventario = "${filters.producto}"`);
      }

      // Filter by tipo (devolucion, solicitud, ambas)
      if (filters.tipo === 'devolucion') {
        filterParts.push('devolucion = true');
      } else if (filters.tipo === 'solicitud') {
        filterParts.push('devolucion != true');
      }
      // Si es 'ambas' o null, no agregamos filtro de tipo

      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : '';

      // Get all matching records (use large page size to get all)
      const result = await this.getList(1, 10000, {
        filter: filterString || undefined,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta,usuario',
        sort: '-created',
      });

      this.logger.debug('Fetched reservas for report', { 
        count: result.items.length,
        filters 
      });
      
      return result.items as ReservaInventarioExpanded[];
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener reservas para reporte: ${error instanceof Error ? error.message : String(error)}`,
        'GET_REPORT_ERROR'
      );
    }
  }
}
